'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  organizationName: string;
  industry: string;
  companySize: string;
  aiReadiness: string;
  primaryChallenge: string;
  transformationGoals: string;
  timeline: string;
}

const PreCallAssessmentForm = () => {
  const [formData, setFormData] = useState<FormData>({
    organizationName: '',
    industry: '',
    companySize: '',
    aiReadiness: '',
    primaryChallenge: '',
    transformationGoals: '',
    timeline: '',
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      const bookingSection = document.getElementById('booking-calendar');
      bookingSection?.scrollIntoView({ behavior: 'smooth' });
    }, 1500);
  };

  if (isSubmitted) {
    return (
      <section className="py-20 lg:py-32 bg-gradient-to-br from-success/5 to-primary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircleIcon" size={48} variant="solid" className="text-success" />
            </div>
            <h3 className="text-3xl font-bold font-headline text-primary mb-4">
              Assessment Complete!
            </h3>
            <p className="text-xl text-muted-foreground font-body mb-8">
              Thank you for providing your organizational context. Now let's schedule your personalized discovery call.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 lg:py-32 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-headline text-primary mb-6">
              Pre-Call Assessment
            </h2>
            <p className="text-xl text-muted-foreground font-body">
              Help us understand your organization's context to personalize your discovery call experience.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-card rounded-2xl p-8 lg:p-12 shadow-strong">
            <div className="space-y-6">
              <div>
                <label htmlFor="organizationName" className="block text-sm font-semibold text-foreground mb-2">
                  Organization Name *
                </label>
                <input
                  type="text"
                  id="organizationName"
                  name="organizationName"
                  value={formData.organizationName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                  placeholder="Your company name"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="industry" className="block text-sm font-semibold text-foreground mb-2">
                    Industry *
                  </label>
                  <select
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                  >
                    <option value="">Select industry</option>
                    <option value="financial-services">Financial Services</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="retail">Retail & E-commerce</option>
                    <option value="technology">Technology</option>
                    <option value="professional-services">Professional Services</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="companySize" className="block text-sm font-semibold text-foreground mb-2">
                    Company Size *
                  </label>
                  <select
                    id="companySize"
                    name="companySize"
                    value={formData.companySize}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                  >
                    <option value="">Select size</option>
                    <option value="50-200">50-200 employees</option>
                    <option value="200-1000">200-1,000 employees</option>
                    <option value="1000-5000">1,000-5,000 employees</option>
                    <option value="5000+">5,000+ employees</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="aiReadiness" className="block text-sm font-semibold text-foreground mb-2">
                  AI Readiness Level *
                </label>
                <select
                  id="aiReadiness"
                  name="aiReadiness"
                  value={formData.aiReadiness}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                >
                  <option value="">Select readiness level</option>
                  <option value="exploring">Exploring - Just starting to consider AI</option>
                  <option value="planning">Planning - Developing AI strategy</option>
                  <option value="piloting">Piloting - Running initial AI projects</option>
                  <option value="scaling">Scaling - Expanding AI across organization</option>
                </select>
              </div>

              <div>
                <label htmlFor="primaryChallenge" className="block text-sm font-semibold text-foreground mb-2">
                  Primary Transformation Challenge *
                </label>
                <select
                  id="primaryChallenge"
                  name="primaryChallenge"
                  value={formData.primaryChallenge}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                >
                  <option value="">Select primary challenge</option>
                  <option value="leadership-alignment">Leadership alignment and buy-in</option>
                  <option value="cultural-resistance">Cultural resistance to change</option>
                  <option value="skills-gap">Skills gap and training needs</option>
                  <option value="roi-measurement">ROI measurement and tracking</option>
                  <option value="implementation-execution">Implementation and execution</option>
                  <option value="vendor-selection">Technology and vendor selection</option>
                </select>
              </div>

              <div>
                <label htmlFor="transformationGoals" className="block text-sm font-semibold text-foreground mb-2">
                  Transformation Goals
                </label>
                <textarea
                  id="transformationGoals"
                  name="transformationGoals"
                  value={formData.transformationGoals}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                  placeholder="Describe your key objectives for AI transformation..."
                />
              </div>

              <div>
                <label htmlFor="timeline" className="block text-sm font-semibold text-foreground mb-2">
                  Desired Timeline *
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                >
                  <option value="">Select timeline</option>
                  <option value="immediate">Immediate - Within 1 month</option>
                  <option value="short-term">Short-term - 1-3 months</option>
                  <option value="medium-term">Medium-term - 3-6 months</option>
                  <option value="long-term">Long-term - 6+ months</option>
                </select>
              </div>
            </div>

            <div className="mt-8">
              <button
                type="submit"
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-semibold font-cta text-base text-white bg-secondary hover:bg-secondary/90 transition-all duration-300 shadow-strong hover:shadow-xl hover:-translate-y-0.5"
              >
                Continue to Booking
                <Icon name="ArrowRightIcon" size={20} variant="solid" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default PreCallAssessmentForm;